// Shared WebSocket protocol for Nexus Forge.
//
// Every wire message conforms to the {type, ts, payload} envelope from
// CONTRACTS §2. Builder functions below are the ONLY sanctioned way to
// construct a wire message — check-contracts.ts §2 enforces that every
// `.send()` call passes either a typed variable or a build*Message call.

import type { BaseItemId, ItemId } from "./items.ts";
export type { MarketView } from "./market.ts"; // surfaced for snapshot.payload.market consumers

// Workshop types and message builders live in workshop-messages.ts to keep
// this file under the §10 400-line cap. Re-exported here for one-stop imports.
export type {
  JobStatus,
  WorkshopJob,
  WorkshopState,
  ClientQueueCraft,
  ClientCancelCraft,
  ClientClaimCompletedJobs,
  ClientGetWorkshop,
  ServerCraftQueued,
  ServerCraftCancelled,
  ServerJobStarted,
  ServerJobCompleted,
  ServerWorkshopUpdated,
} from "./workshop-messages.ts";
export {
  buildQueueCraftMessage,
  buildCancelCraftMessage,
  buildClaimCompletedJobsMessage,
  buildGetWorkshopMessage,
  buildCraftQueuedMessage,
  buildCraftCancelledMessage,
  buildJobStartedMessage,
  buildJobCompletedMessage,
  buildWorkshopUpdatedMessage,
} from "./workshop-messages.ts";
import type {
  ClientQueueCraft,
  ClientCancelCraft,
  ClientClaimCompletedJobs,
  ClientGetWorkshop,
  ServerCraftQueued,
  ServerCraftCancelled,
  ServerJobStarted,
  ServerJobCompleted,
  ServerWorkshopUpdated,
} from "./workshop-messages.ts";

// Market wire types and builders live in market-messages.ts (Phase 4
// Step 6). Bulk re-export keeps this barrel narrow; local imports
// below pull in just what the ClientMessage/ServerMessage unions need.
export * from "./market-messages.ts";
import type {
  ClientListItem,
  ClientCancelListing,
  ClientBuyListing,
  ClientNpcSell,
  ClientNpcBuy,
  ClientGetMarketView,
  ClientGetPriceHistory, ClientRequestRemoteMarketView,
  ServerMarketView,
  ServerListingCreated,
  ServerListingCancelled,
  ServerListingSold,
  ServerCreditsUpdated,
  ServerPriceHistory, ServerRemoteMarketView,
} from "./market-messages.ts";

// Resume-protocol wire types + builders (Phase 5 Step 2).
export * from "./resume-messages.ts";
import type { ClientResume, ServerReplay, ServerSnapshot } from "./resume-messages.ts";

// Combat wire types + builders (Phase 5 Step 6). Shape-only for now;
// Step 9 registers the useAbility handler on ExpeditionDO.
export * from "./combat-messages.ts";
import type {
  ClientUseAbility,
  ServerAbilityResolved,
  ServerAbilityMissed,
  ServerDamageDealt,
  ServerDamageTaken,
  ServerPlayerDied,
  ServerMobSpawned,
  ServerMobDefeated,
} from "./combat-messages.ts";

export * from "./durability-messages.ts"; import type { ClientRepairItem, ServerItemRepaired } from "./durability-messages.ts"; // Phase 5 Step 10/18
export * from "./equip-messages.ts"; import type { ClientEquipItem, ClientUnequipItem, ServerEquippedItemUpdated } from "./equip-messages.ts"; // Phase 7 Block C

// Station / jump-gate wire types + builders (Phase 6 Block A).
export * from "./station-messages.ts"; import type { ClientRequestJump, ServerJumpStarted, ServerJumpCompleted, ServerTraveling } from "./station-messages.ts";
// Skill-tier notification (Phase 6 Block B) + xpGranted (Phase 7 Block E).
export * from "./skill-messages.ts"; import type { ServerSkillTierReached, ServerXpGranted } from "./skill-messages.ts";
export * from "./display-name-messages.ts"; import type { ClientSetDisplayName, ServerDisplayNameSet } from "./display-name-messages.ts"; // Phase 7 Block B
export * from "./friends-messages.ts"; import type { ClientRequestNearbyPlayers, ClientAddFriend, ClientRemoveFriend, ServerNearbyPlayers, ServerFriendsListUpdated } from "./friends-messages.ts"; // Phase 7 Block F
// Expedition + party wire types + builders (Phase 5 Step 11+).
export * from "./expedition-messages.ts";
import type {
  ClientInviteToParty, ClientAcceptPartyInvite, ClientDeclinePartyInvite, ClientLeaveParty,
  ClientExitExpedition, ClientEnterExpedition, ClientAcceptExpeditionReady, ClientDeclineExpeditionReady,
  ServerPartyInvite, ServerPartyMemberJoined, ServerPartyMemberLeft, ServerPartyDissolved,
  ServerExpeditionReady, ServerExpeditionEntered, ServerExpeditionReadyConfirmed, ServerExpeditionExited,
  ServerLootRolled, ServerExpeditionCompleted, ServerExpeditionPositions,
} from "./expedition-messages.ts";

export type PlayerState = {
  playerId: string;
  x: number;
  y: number;
  color: string; // hex like "#ff6600"; assigned on first getState
  displayName: string; // Phase 7 Block B — "" = unset
};

// One row of a player's inventory. `slotIndex` is the 0-based position
// within the grid. Empty slots are represented by the absence of a
// row, not by `count: 0` — callers iterate only occupied slots and
// compute the empty ones from `balance.inventory.maxSlots`.
// Step 18: `rowid`, `durabilityCurrent`, `durabilityMax` are populated
// only for durability-bearing rows (server-side `durability_max != -1`).
// The Repair tab keys off rowid to address the specific instance.
export type InventorySlot = {
  slotIndex: number;
  itemId: ItemId;
  count: number;
  rowid?: number;
  durabilityCurrent?: number;
  durabilityMax?: number;
};

// One row of a player's skill XP. skillId is a member of SKILL_IDS from
// shared/skills.ts — unknown IDs are rejected at the RPC boundary (Phase 3+).
export type SkillEntry = {
  skillId: string;
  xp: number;
};

// A resource node's observable state. `respawnAt` is null when the
// node is active (has remaining yield) and set to an epoch-ms
// timestamp while the node is depleted and waiting for its respawn
// alarm to fire.
export type ResourceNode = {
  nodeId: string;
  itemId: BaseItemId; // nodes always yield raw resources — never quality-suffixed
  x: number;
  y: number;
  remainingYield: number;
  respawnAt: number | null;
};

// -----------------------------------------------------------------------
// Envelope + discriminated unions
// -----------------------------------------------------------------------

export type Envelope<T extends string, P> = {
  type: T;
  // Milliseconds since epoch at message creation time. Fills in
  // automatically inside the builders.
  ts: number;
  // Payload is always an object per CONTRACTS §2. Empty payloads are `{}`.
  payload: P;
  // Phase 5 Step 3 — durable wire augmentation. Durable envelopes
  // carry both; ephemeral and handshake meta-envelopes carry neither.
  seq?: number;
  eventId?: string;
};

// Client -> Server
export type ClientMove = Envelope<"move", { x: number; y: number; t: number }>;
export type ClientPing = Envelope<"ping", Record<string, never>>;
// Player wants to start gathering a node. Server validates (in-range,
// node active, no in-flight session) and replies with either
// gatherStarted or gatherCancelled.
export type ClientGatherStart = Envelope<"gatherStart", { nodeId: string }>;
// Player released the gather key. Server cancels any in-flight
// session for this player; no-op if none. Empty payload per §2.
export type ClientGatherCancel = Envelope<"gatherCancel", Record<string, never>>;

export type ClientMessage =
  | ClientMove | ClientPing | ClientSetDisplayName | ClientGatherStart | ClientGatherCancel
  | ClientQueueCraft | ClientCancelCraft | ClientClaimCompletedJobs | ClientGetWorkshop
  | ClientListItem | ClientCancelListing | ClientBuyListing
  | ClientNpcSell | ClientNpcBuy | ClientGetMarketView | ClientGetPriceHistory
  | ClientRequestRemoteMarketView
  | ClientResume | ClientUseAbility
  | ClientInviteToParty | ClientAcceptPartyInvite | ClientDeclinePartyInvite | ClientLeaveParty
  | ClientExitExpedition | ClientEnterExpedition
  | ClientAcceptExpeditionReady | ClientDeclineExpeditionReady
  | ClientRepairItem
  | ClientEquipItem | ClientUnequipItem
  | ClientRequestJump
  | ClientRequestNearbyPlayers | ClientAddFriend | ClientRemoveFriend;

// Server -> Client
export type ServerPlayerJoined = Envelope<"playerJoined", { player: PlayerState }>;
export type ServerPlayerLeft = Envelope<"playerLeft", { playerId: string }>;
export type ServerPlayerMoved = Envelope<
  "playerMoved",
  { playerId: string; x: number; y: number; t: number }
>;
export type ServerPong = Envelope<"pong", { t: number; serverT: number }>;
// `context` carries the offending itemId / listingId from the server
// GameError so clients route error-path pending-clears.
export type ServerError = Envelope<"error", { code: string; message: string; context?: Record<string, unknown> }>;

// Gather lifecycle. The client drives the start/cancel; the server
// is authoritative on validation, completion, and broadcast.
export type ServerGatherStarted = Envelope<
  "gatherStarted",
  { nodeId: string; durationMs: number; expectedCompleteAt: number }
>;
// itemsGained describes a single item+count; Phase 2 always awards
// one unit per completion. The structure leaves room for Phase 3+
// multi-item gathers without a protocol break. `inventory` is the
// full post-gather snapshot so the client never has to reconstruct
// it from deltas.
export type ServerGatherCompleted = Envelope<
  "gatherCompleted",
  {
    nodeId: string;
    itemsGained: { itemId: BaseItemId; count: number }; // gather always yields raw resources
    inventory: InventorySlot[];
  }
>;
// `reason` is a stable machine-readable string. Phase 2 reasons:
//   "moved"          — player moved during the gather
//   "out_of_range"   — player too far from node at start or end
//   "node_depleted"  — node hit zero yield before this gather resolved
//   "not_joined"     — player sent gatherStart without prior join
//   "already_gathering" — player had an in-flight session
//   "invalid_node"   — nodeId not in the zone
// Phase 3+ may add more. Clients branch on the code, display the
// text separately from the server in a user-facing string.
export type ServerGatherCancelled = Envelope<
  "gatherCancelled",
  { nodeId: string; reason: string }
>;
// Broadcast to all clients in the zone when a node's remaining yield
// hits zero. respawnAt is the epoch-ms the respawn alarm is set for;
// clients use it to render a countdown.
export type ServerNodeDepleted = Envelope<
  "nodeDepleted",
  { nodeId: string; respawnAt: number }
>;
// Broadcast to all clients when the respawn alarm fires and the node
// is back to full yield. No payload beyond nodeId — clients re-render
// the node from their static zone template.
export type ServerNodeRespawned = Envelope<"nodeRespawned", { nodeId: string }>;
// Catch-all inventory snapshot for any change not already covered by
// gatherCompleted. Phase 2 rarely uses this; Phase 3+ crafting will.
export type ServerInventoryUpdated = Envelope<
  "inventoryUpdated",
  { inventory: InventorySlot[] }
>;

export type ServerMessage =
  | ServerPlayerJoined | ServerPlayerLeft | ServerPlayerMoved | ServerPong | ServerError
  | ServerGatherStarted | ServerGatherCompleted | ServerGatherCancelled
  | ServerNodeDepleted | ServerNodeRespawned | ServerInventoryUpdated
  | ServerCraftQueued | ServerCraftCancelled | ServerJobStarted | ServerJobCompleted | ServerWorkshopUpdated
  | ServerMarketView | ServerListingCreated | ServerListingCancelled | ServerListingSold | ServerCreditsUpdated | ServerPriceHistory
  | ServerReplay | ServerSnapshot
  | ServerAbilityResolved | ServerAbilityMissed | ServerDamageDealt | ServerDamageTaken
  | ServerPlayerDied
  | ServerMobSpawned
  | ServerMobDefeated
  | ServerItemRepaired
  | ServerPartyInvite
  | ServerPartyMemberJoined
  | ServerPartyMemberLeft
  | ServerPartyDissolved
  | ServerExpeditionReady
  | ServerExpeditionEntered
  | ServerExpeditionReadyConfirmed
  | ServerExpeditionExited
  | ServerLootRolled
  | ServerExpeditionCompleted
  | ServerExpeditionPositions
  | ServerJumpStarted | ServerJumpCompleted | ServerTraveling
  | ServerSkillTierReached | ServerXpGranted | ServerRemoteMarketView | ServerDisplayNameSet
  | ServerEquippedItemUpdated
  | ServerNearbyPlayers | ServerFriendsListUpdated;

// Builders — one per message type. Contracts §2 only accepts builder
// calls matching /^(build|make|create).*Message$/ in WebSocket sends.

export const buildMoveMessage = (x: number, y: number, t: number): ClientMove => ({
  type: "move",
  ts: Date.now(),
  payload: { x, y, t },
});

export const buildPingMessage = (): ClientPing => ({
  type: "ping",
  ts: Date.now(),
  payload: {},
});

export const buildPlayerJoinedMessage = (player: PlayerState): ServerPlayerJoined => ({
  type: "playerJoined",
  ts: Date.now(),
  payload: { player },
});

export const buildPlayerLeftMessage = (playerId: string): ServerPlayerLeft => ({
  type: "playerLeft",
  ts: Date.now(),
  payload: { playerId },
});

export const buildPlayerMovedMessage = (
  playerId: string,
  x: number,
  y: number,
  t: number,
): ServerPlayerMoved => ({
  type: "playerMoved",
  ts: Date.now(),
  payload: { playerId, x, y, t },
});

export const buildPongMessage = (clientTs: number, serverTs: number): ServerPong => ({
  type: "pong",
  ts: Date.now(),
  payload: { t: clientTs, serverT: serverTs },
});

// Conditional spread keeps context genuinely absent under
// exactOptionalPropertyTypes — not `context: undefined`.
export const buildErrorMessage = (code: string, message: string, context?: Record<string, unknown>): ServerError => ({
  type: "error",
  ts: Date.now(),
  payload: context !== undefined ? { code, message, context } : { code, message },
});

export const buildGatherStartMessage = (nodeId: string): ClientGatherStart => ({
  type: "gatherStart",
  ts: Date.now(),
  payload: { nodeId },
});

export const buildGatherCancelMessage = (): ClientGatherCancel => ({
  type: "gatherCancel",
  ts: Date.now(),
  payload: {},
});

export const buildGatherStartedMessage = (
  nodeId: string,
  durationMs: number,
  expectedCompleteAt: number,
): ServerGatherStarted => ({
  type: "gatherStarted",
  ts: Date.now(),
  payload: { nodeId, durationMs, expectedCompleteAt },
});

export const buildGatherCompletedMessage = (
  nodeId: string,
  itemsGained: { itemId: BaseItemId; count: number },
  inventory: InventorySlot[],
): ServerGatherCompleted => ({
  type: "gatherCompleted",
  ts: Date.now(),
  payload: { nodeId, itemsGained, inventory },
});

export const buildGatherCancelledMessage = (
  nodeId: string,
  reason: string,
): ServerGatherCancelled => ({
  type: "gatherCancelled",
  ts: Date.now(),
  payload: { nodeId, reason },
});

export const buildNodeDepletedMessage = (
  nodeId: string,
  respawnAt: number,
): ServerNodeDepleted => ({
  type: "nodeDepleted",
  ts: Date.now(),
  payload: { nodeId, respawnAt },
});

export const buildNodeRespawnedMessage = (nodeId: string): ServerNodeRespawned => ({
  type: "nodeRespawned",
  ts: Date.now(),
  payload: { nodeId },
});

export const buildInventoryUpdatedMessage = (
  inventory: InventorySlot[],
): ServerInventoryUpdated => ({
  type: "inventoryUpdated",
  ts: Date.now(),
  payload: { inventory },
});
export const DEFAULT_ZONE_ID = "station-7:hub"; // string constant, not a tuning value
